/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Component, FormEvent } from 'react';
import { AppState } from '../../app.state';
import { User, _User } from '../../_shared/interfaces/user';
import { Alert, Modal, message } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router';
import FormTitle from '../../_shared/form-title';
import styles from '../../_shared/styles';
import { Button } from '../../_shared/button';
import {
  buildErrorMsgFromForm,
  uppercaseFirst,
  validPhoneNumber,
  userHasRole,
  isServiceOrgAdmin,
  serviceOrgThenDentalOrgs,
  sortByLabel,
  cleanPhoneNumber,
  cleanCopy,
  displayPhoneNumber,
  uppercaseWords,
  isNativeAppBrowser,
  wait,
} from '../../utils';
import Link from '../../_shared/link';
import { get, set } from 'lodash';
import { getRolesAvailableForUser } from '../add-user/add-user.component';
import {
  _ModalVerify,
  ModalVerify,
} from '../../_shared/modal-verify/modal-verify.component';
import { WithForm } from '../../_shared/form';
import { WithAppConfig } from '../../_shared/hooks/useAppConfig';
import { trackEvent, } from '../../_shared/services/analytics.service';
import eventTypes from '../../_shared/interfaces/api/eventTypes';
import { deleteOwnAccount } from '../../_shared/services/manage-users.service';
import {
  getCognitoAccessToken,
  signOut,
} from '../../_shared/services/authentication.service';
import { clearStorage } from '../dashboard.actions';
import colors from '../../_shared/colors';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateUser: (user: User, shouldUpdateCurrent: boolean) => Promise<User>;
  getUserData: () => Promise<{}>;
  form?: any;
  app?: any;
}

export class _AccountDetailsComponent extends Component<IProps> {
  state = {
    isEdit: false,
    canEditRole: false,
    loading: false,
    user: undefined,
    back: undefined,
    toDisplay: [
      {
        title: 'Role',
        var: 'role',
        type: 'dropdown',
        disabled: () => {
          const { sState } = this.props;
          const { user, isEdit } = this.state;

          const uState = { auth: { user } } as any;
          const currentIsSuper = userHasRole([0], sState);
          const currentIsTech = userHasRole([1], sState);
          const userIsSuperTech = userHasRole([0, 1], uState);
          const currentIsOrgAdmin = userHasRole([2], sState);

          const areTheSame =
            get(sState, 'auth.user.userId') === get(uState, 'auth.user.userId');

          if (!isEdit) {
            return true;
          } else if (currentIsSuper) {
            return false;
          } else if (areTheSame) {
            return true;
          } else if (currentIsTech) {
            return userIsSuperTech ? true : false;
          } else if (currentIsOrgAdmin) {
            const currentOrg = get(sState, 'auth.user.orgId');
            const userOrg = get(uState, 'auth.user.orgId');
            if (currentOrg && currentOrg === userOrg) {
              return false;
            } else {
              return true;
            }
          } else {
            return true;
          }
        },
        opts: () => getRolesAvailableForUser(this.props.sState),
        options: {
          rules: [
            {
              required: true,
              message: 'Role is required. ',
            },
          ],
        },
      },
      {
        title: 'Equipment Types',
        var: 'eqTypeOptions',
        type: 'dropdown',
        disabled: () => !this.state.isEdit,
        canDisplay: () => {
          const {
            form: { getFieldValue },
          } = this.props;

          return getFieldValue('role') === 5;
        },
        opts: () => [
          { title: 'Utility', value: 'utility' },
          { title: 'Handpiece', value: 'handpiece' },
          { title: 'Equipment', value: 'chair' },
        ],
        props: {
          mode: 'multiple',
        },
      },
      {
        title: 'Organization',
        var: 'orgId',
        type: 'dropdown',
        disabled: () => !this.state.isEdit,
        canDisplay: () => {
          const { form, sState } = this.props;
          const hasRole = userHasRole([0, 1], sState);
          const isSOAdmin = isServiceOrgAdmin(sState);
          const formrole = parseInt(form.getFieldValue('role'));
          const hiddenroles = [7, 5];
          return (hasRole || isSOAdmin) &&
            formrole >= 2 &&
            hiddenroles.indexOf(formrole) === -1
            ? true
            : false;
        },
        transform: (val: string) => {
          const { sState } = this.props;
          if (userHasRole([0, 1], sState) && !!val) {
            return val;
          } else {
            return sState.auth.user && sState.auth.user.orgId
              ? sState.auth.user.orgId
              : get(this, 'state.user.orgId', val);
          }
        },
        placeholder: 'Select',
        opts: () => {
          const { sState } = this.props;
          const isSOAdmin = isServiceOrgAdmin(sState);

          if (isSOAdmin) {
            return serviceOrgThenDentalOrgs(sState);
          } else {
            return sState.dash.orgs
              .map(o => ({ title: o.name, value: o.id, label: o.name }))
              .sort(sortByLabel);
          }
        },
        options: () => {
          const { form } = this.props;
          const isRole =
            parseInt(form.getFieldValue('role')) >= 2 ? true : false;
          if (isRole) {
            return {
              rules: [
                {
                  required: true,
                  message: 'Organization is required. ',
                },
              ],
            };
          }
          return undefined;
        },
      },
      {
        title: 'Branch Location',
        var: 'branchLocation',
        disabled: () => !this.state.isEdit,
        canDisplay: () => {
          const { form } = this.props;
          const org = form.getFieldValue('orgId');
          return !!org && parseInt(form.getFieldValue('role')) == 6;
        },
        options: {
          rules: [
            {
              max: 80,
              message: 'Branch is limited to 80 characters',
            },
          ],
        },
      },
      {
        title: 'Name',
        var: 'name',
        disabled: () => !this.state.isEdit,
        transform: (value: string) => uppercaseWords(value),
        options: {
          rules: [
            {
              required: true,
              message: 'Name is required. ',
            },
            {
              max: 50,
              message: 'Name is limited to 50 characters. ',
            },
          ],
        },
      },
      {
        title: 'Email',
        var: 'email',
        alwaysDisabled: true,
        transform: (value: string) => (value || '').toLowerCase(),
        options: {
          rules: [
            {
              required: true,
              message: 'Email is required',
            },
          ],
        },
      },
      {
        title: 'Phone',
        var: 'phone',
        disabled: () => !this.state.isEdit,
        props: {},
        val: (user: User) => displayPhoneNumber(get(user, 'phone')),
        transform: (value: string) => cleanPhoneNumber(value),
        options: {
          validateTrigger: 'onBlur',
          rules: [
            {
              // tslint:disable-next-line:no-any
              validator: (rule: any, value: string, cb: any) => {
                const passes = validPhoneNumber(value);
                cb(!value || passes ? undefined : rule.message);
              },
              message: 'Phone needs to be a valid phone number. ',
            },
          ],
        },
      },
    ],
  };

  onSubmit = (e: MouseEvent | FormEvent) => {
    e.preventDefault();
    if (this.state.loading) {
      return;
    }

    const { getFieldValue } = get(this.props, 'form');
    const phone = getFieldValue('phone');
    const { user } = this.state;
    e.preventDefault();
    if (this.state.loading) {
      return;
    }
    if (!phone && get(user, 'mfa') === 'sms') {
      return this.props.app.modal.confirm({
        title: '2FA Currently Set to SMS',
        content:
          //tslint:disable-next-line:max-line-length
          'Two-Factor Authentication for this user is currently set to SMS.  If you remove the phone number associated with this account, 2FA will be updated to use the email address instead.',
        okText: 'Update',
        onCancel: () => 0,
        onOk: this.submit,
      });
    }
    this.submit();
  };

  submit = async () => {
    if (this.state.loading) {
      return;
    }

    this.state.loading = true;
    this.setState(this.state);

    this.props.form.validateFieldsAndScroll(async (err: any, values: any) => {
      if (err) {
        const error = buildErrorMsgFromForm(err);
        message.error(error);
        return this.setState({ loading: false });
      }

      try {
        const originalUser = cleanCopy(this.state.user);

        const ouser = new _User({
          ...(this.state.user || {}),
        } as User);
        const user = new _User({
          ...(this.state.user || {}),
        } as User);

        this.state.toDisplay.map(item => {
          const val = values[item.var];
          user[item.var] = item.transform ? item.transform(val) : val;
        });

        if (userHasRole([0, 1], ({ auth: { user } } as unknown) as AppState)) {
          user.orgId = '';
        }

        const isSuper = userHasRole([0], ({
          auth: { user },
        } as unknown) as AppState);
        if (
          isSuper &&
          user.phoneVerified &&
          user.phone &&
          ouser.phone !== user.phone
        ) {
          const mverify = get(this, 'MODALVERIFY');
          const valid =
            mverify &&
            mverify.openModal &&
            (await mverify.openModal({
              phone: user.phone,
            }));
          // AER-1628: only overwrite phone/verified when the modal actually
          // returned a phone. If the user cancelled, or the modal was
          // unavailable, keep the original phone — do NOT wipe it.
          const newPhone = get(valid, 'phone');
          if (newPhone) {
            set(user, 'phone', newPhone);
            set(user, 'phoneVerified', true);
          }
        }

        if (
          isSuper &&
          user.phoneVerified &&
          ouser.phone &&
          !user.phone &&
          ouser.mfa === 'sms'
        ) {
          set(user, 'phoneVerified', false);
          set(user, 'mfa', 'email');
        }

        if (ouser.orgId !== user.orgId) {
          set(user, 'equipment', []);
          set(user, 'groups', []);
          set(user, 'locations', []);
        }

        const { sState } = this.props;
        const me: any = get(sState, 'auth.user.userId');
        const shouldUpdateCurrent = me === user.userId;

        await this.props.updateUser(user, shouldUpdateCurrent);

        trackEvent(eventTypes.user_update, { old: originalUser, new: user });
        message.success('Data saved successfully!');
        this.setState(
          {
            loading: false,
            isEdit: false,
            user,
          },
          this.setFieldsOriginal
        );
        this.props.getUserData();
      } catch (err) {
        message.error('Save failed: ' + err.message);
        return this.setState({
          loading: false,
        });
      }
    });
  };
  componentDidMount = () => {
    const {
      sState: {
        auth: { user },
      },
      history: { location },
    } = this.props;

    let _user = user;
    let back = undefined;
    let isEdit = this.state.isEdit;
    const lstate = location && (location.state as any);
    if (lstate && lstate.user) {
      _user = lstate.user;
      back = lstate.back;
      isEdit = lstate.editMode || isEdit;
    }

    this.setState(
      {
        user: _user,
        back,
        isEdit,
      },
      this.setFieldsOriginal
    );
  };
  setFieldsOriginal = () => {
    const setFields = () => {
      const { toDisplay, user } = this.state;
      const {
        form: { setFieldsValue },
      } = this.props;

      const User = new _User({
        ...(user || {}),
      } as User);

      toDisplay.map(item => {
        setFieldsValue({
          [item.var]: item.val ? item.val(User) : User[item.var],
        });
      });
    };
    setFields();
    //we are doing this becuase in some instasnces org might not be shown
    //until after the first render. so the field can't be set if it isn't yet shown
    setTimeout(setFields, 200);
  };
  backClick = () => {
    this.props.history.goBack();
  };

  // AER-1819: self-service account deletion, which the iOS / Android stores require. Offered
  // only inside the aeras-native app (its WebView user agent carries `aerasnative/`), and only
  // on the signed-in user's own Account Details — never on another user opened from Manage
  // Users, which arrives here with a `back` link.
  canDeleteOwnAccount = () => {
    const { back } = this.state;
    const me = get(this.props.sState, 'auth.user.email', '');
    const shown = get(this.state, 'user.email', '');
    return (
      isNativeAppBrowser() &&
      !back &&
      !!me &&
      `${me}`.toLowerCase() === `${shown}`.toLowerCase()
    );
  };

  deleteAccount = () => {
    this.props.app.modal.confirm({
      title: 'Confirmation: Delete Account',
      content:
        'Are you sure you want to delete your user account? You will immediately be signed out and will no longer be able to access your account. This can not be reversed.',
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: this.confirmDeleteAccount,
    });
  };

  confirmDeleteAccount = async () => {
    try {
      const accessToken = await getCognitoAccessToken();
      if (!accessToken) {
        message.error(
          'For your security, please sign out and sign back in, then delete your account.'
        );
        return;
      }
      await deleteOwnAccount(accessToken);
    } catch (err) {
      message.error(
        `Your account could not be deleted. ${get(err, 'message', '')}`
      );
      return;
    }

    // Audit trail, as Admin > Users' delete keeps one. Capped so it cannot hold up the sign-out.
    await Promise.race([trackEvent(eventTypes.user_delete, { self: true }), wait(2000)]);

    // The account is gone. Sign out the way logout() does (dashboard.actions.ts), minus its
    // POST /user/activity signedOutAt write, which has no record left to stamp. The hard
    // redirect to /logout is also what aeras-native watches for: it clears its own session
    // and OneSignal login and reloads to the Sign In screen.
    try {
      await signOut();
    } catch (e) {
      console.warn('AER-1819: Cognito signOut after account deletion failed', get(e, 'message'));
    }
    await clearStorage();
    window.location.href = window.location.origin + '/logout';
  };

  MODALVERIFY: _ModalVerify | undefined;
  render() {
    const { toDisplay, isEdit, loading, back } = this.state;
    const { Form } = this.props.form;

    const user = (this.state.user as unknown) as User;

    return user ? (
      <div css={css(styles.formContainer)}>
        {!!back && (
          <Link
            css={css`
              margin-bottom: 5px;
            `}
            onClick={this.backClick}
          >
            {back}
          </Link>
        )}

        <FormTitle
          size={'20px'}
          title={
            !!back && isEdit
              ? 'Edit User'
              : `${isEdit ? 'Edit ' : ''}Account Details`
          }
        />

        <Form
          layout="vertical"
          onSubmit={this.onSubmit}
          items={toDisplay}
        >
          {!isEdit && (
            <Button
              title={'Edit'}
              onClick={() => this.setState({ isEdit: true })}
            />
          )}
          {isEdit && (
            <Button
              title={'Cancel'}
              disabled={loading}
              css={css`
                margin-right: 10px;
              `}
              outline={true}
              onClick={() => {
                if (back) {
                  this.backClick();
                } else {
                  this.setFieldsOriginal();
                  this.setState({ isEdit: false });
                }
              }}
            />
          )}
          {isEdit && (
            <Button title={'Save'} loading={loading} onClick={this.onSubmit} />
          )}
          <ModalVerify
            wrappedComponentRef={(comp: _ModalVerify) =>
              set(this, 'MODALVERIFY', comp)
            }
          />
        </Form>
        {!isEdit && this.canDeleteOwnAccount() && (
          <div
            css={css`
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid ${colors.hrBorder};
            `}
          >
            <Button
              title={'Delete Account'}
              danger={true}
              onClick={this.deleteAccount}
            />
          </div>
        )}
      </div>
    ) : (
      <div>No User Provided</div>
    );
  }
}

export const AccountDetailsComponent = WithAppConfig(WithForm(withRouter(_AccountDetailsComponent)));
